import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';

import Logo from '../../components/shared/Logo/Logo';
import useAuth from '../../hooks/useAuth';
import { useTranslation } from '../../hooks/useTranslation';
import { redeemIframeToken } from '../../services/mixvoipService';
import {
  getPartnerSession,
  savePartnerSession,
} from '../../services/partnerSession';

/**
 * Landing page for the MixVoIP cockpit iframe.
 *
 * The cockpit calls our /auth endpoint, gets back a signed URL and loads it
 * here. We swap the one-time token for an Elora session (identity comes from
 * the token's own claims, not the URL — see mixvoipService), hydrate
 * AuthContext with the real user (so the /dashboard route guard has someone
 * to check permissions for) and drop the user on the dashboard; from then on
 * apiService uses that session for every call.
 */
const MixVoIPEntry: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { loadPartnerSessionUser } = useAuth();

  const token = searchParams.get('token');

  const [isReady, setIsReady] = useState(() => getPartnerSession() !== null);
  const [error, setError] = useState(false);

  // The token is single use: a second redeem is rejected. StrictMode runs
  // effects twice in development, and a re-render must not spend it again, so
  // the attempt is guarded by a ref rather than by state.
  const redeemAttempted = useRef(false);

  useEffect(() => {
    if (isReady) return;

    if (!token) {
      setError(true);
      return;
    }
    if (redeemAttempted.current) return;
    redeemAttempted.current = true;

    redeemIframeToken(token)
      .then(async session => {
        savePartnerSession(session);
        // The /dashboard route guard checks AuthContext.currentUser for
        // permissions; without a userId to resolve that against, there's no
        // one for it to check, so treat that the same as a bad token.
        const gotUser = await loadPartnerSessionUser();
        if (!gotUser) {
          setError(true);
          return;
        }
        setIsReady(true);
      })
      .catch(() => setError(true));
  }, [token, isReady, loadPartnerSessionUser]);

  if (isReady) {
    return <Navigate to="/dashboard" replace />;
  }

  if (error) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ height: '100vh' }}
      >
        <Logo />
        <Typography variant="h6" sx={{ mt: 2 }}>
          {t('This session link is no longer valid')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('Please reopen Elora from your cockpit to continue.')}
        </Typography>
      </Stack>
    );
  }

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{ height: '100vh' }}
    >
      <CircularProgress />
    </Box>
  );
};

export default MixVoIPEntry;
